import React, { useState, useEffect } from "react";
import { Container, Row, Col, Dropdown, Table } from "react-bootstrap";
import { useNavigate } from "react-router";
import { fetchCsrfToken } from "../../../cms-csrftoken"

import { getConfig } from "@edx/frontend-platform"

import { ChevronDown, ChevronRight, X } from "lucide-react";
import { useParams } from "react-router";
import { base_url } from "../../../compugrade-constants";

const StudentDashboard = ({ classData, studentName }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedCourseName, setSelectedCourseName] = useState("");
  const [lessonsFromAPI, setLessonsFromAPI] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [loadingProgress, setLoadingProgress] = useState({
    course: 0,
    average: 0,
  });
  const [courseIntegrationData, setCourseIntegrationData] = useState(null);
  const [reportPopup, setReportPopup] = useState({ open: false, imageUrl: "", title: "" });

  const getProgressColor = (value) => {
    if (value < 50) return "#dc3545";
    if (value <= 70) return "#fd7e14";
    return "#28a745";
  };

  const fetchCourseIntegration = async (courseKey) => {
    if (!courseKey) return null;
     const token = await fetchCsrfToken();
    
    try {
      const res = await fetch(`${getConfig().STUDIO_BASE_URL}/myplugin/course-integration/?course_key=${encodeURIComponent(courseKey)}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": token,
        },
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Failed to get course integration: ${res.status} ${t}`);
      }
      const data = await res.json();
      return data;
    } catch (e) {
      console.error("course-integration error", e);
      return null;
    }
  };

  const handleSelectCourse = (course) => {
    setSelectedCourseId(course.id);
    setSelectedCourseName(course.display_name);
  };

  useEffect(() => {
    if (!selectedCourseId && classData?.courses?.length) {
      handleSelectCourse(classData.courses[0]);
    }
  }, [classData, selectedCourseId]);

  useEffect(() => {
    if (!selectedCourseId || !studentId) return;

    const fetchCourseData = async () => {
      setExpandedSections({})
      setLessonsFromAPI([]);
      setLoadingProgress({ course: 0, average: 0 });
      
      try {
        // First fetch course integration data
        const integrationData = await fetchCourseIntegration(selectedCourseId);
        setCourseIntegrationData(integrationData);

        // Then fetch course progress with integration data in body
        const res = await fetch(
          `${base_url}/api/grading/get_course_progress_for_user`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              course_id: selectedCourseId,
              user_id: studentId,
              content_data: integrationData
            })
          }
        );

        const result = await res.json();
        console.log("Course Progress Result:", result);
        const { overall_progress, overall_score, subsections } = result.data;

        setLoadingProgress({
          course: overall_progress,
          average: overall_score,
        });

        const mappedLessons = subsections.length > 0  && subsections.map((sub) => ({
          section: sub.subsection_title,
          sectionKey: sub.subsection_id,
          targetProgress: sub.average_progress,
          items: sub.rubrics.map((rubric) => ({
            name: rubric.rubric_title,
            targetProgress: rubric.progress,
            lastAttempt: "--",
            grade: rubric.score > 0 ? `${rubric.score}%` : "--",
            dueDate: rubric.due_date
              ? new Date(rubric.due_date).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "",
            letterGrade: rubric.score >= 50 ? "Pass" : "--",
            subrubrics: rubric.subrubrics || []
          })),
        }));

        setLessonsFromAPI(mappedLessons);

        // initialize expanded state
        const newExpanded = {};
        mappedLessons.forEach((s) => {
          newExpanded[s.sectionKey] = true;
        });
        setExpandedSections(newExpanded);
      } catch (error) {
        console.error("Failed to fetch course data", error);
      }
    };

    fetchCourseData();
  }, [selectedCourseId, studentId]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const openReportPopup = (imageUrl, title) => {
    setReportPopup({ open: true, imageUrl, title });
  };

  const closeReportPopup = () => {
    setReportPopup({ open: false, imageUrl: "", title: "" });
  };

  const AnimatedCircularProgress = ({
    percentage,
    color,
    label,
    size = 100,
    strokeWidth = 8,
    labelColor = "#333",
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset =
      circumference - (percentage / 100) * circumference;

    return (
      <div style={{ textAlign: "center", margin: "0 15px" }}>
        <div style={{ position: "relative", display: "inline-block" }}>
          <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke="#e9ecef"
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={color}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{
                transition: "stroke-dashoffset 0.3s ease-out",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
              }}
            />
          </svg>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "14px",
              fontWeight: "bold",
              color: labelColor,
            }}
          >
            {Number.isFinite(percentage) ? percentage.toFixed(2) : "0.00"}%
          </div>
        </div>
        {label && (
          <div
            style={{
              marginTop: "8px",
              fontSize: "11px",
              color: "#6c757d",
              fontWeight: "500",
            }}
          >
            {label}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!selectedCourseId && classData?.courses?.length) {
      handleSelectCourse(classData.courses[0]);
    }
  }, [classData, selectedCourseId]);

  return (
    <div className="h-auto mb-4" style={{ borderRadius: "5px", border: "0.5px solid rgba(0, 0, 0, 0.30)", padding: "16px" }}>
      <Container fluid>
        <Row className="mb-3">
          <Col md={8}>
            <h4 className="primary-text">
              {classData?.name} {">"} {studentName} {">"} {selectedCourseName}
            </h4>
          </Col>
          <Col md={4} className="text-end">
            <div className="d-flex align-items-center justify-content-end">
              <span className="me-2" style={{ fontSize: "14px", fontWeight: "500", color: "#495057" }}>
                Select Course :
              </span>
              <Dropdown>
                <Dropdown.Toggle className="outline-none" variant="outline-secondary" size="sm" style={{ minWidth: "100px", textAlign: "left", fontSize: "13px" }}>
                  {selectedCourseName || "Select Course"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {classData?.courses?.map((course) => (
                    <Dropdown.Item key={course.id} onClick={() => handleSelectCourse(course)}>
                      {course.display_name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Col>
        </Row>

        {selectedCourseName && (
          <>
            <Row className="mb-4 px-3 py-2" style={{ borderRadius: "2px", background: "#F1F1F1" }}>
              <Col md={7} className="mt-2">
                <h5 style={{ fontWeight: "600", marginBottom: "12px", fontSize: "15px", color: "#212529" }}>
                  Course Progress
                </h5>
                <h6 style={{ fontSize: "13px", color: "#495057", marginBottom: "16px" }}>
                  {studentName} has completed{" "}
                  <span style={{ color: getProgressColor(loadingProgress.course), fontWeight: "600" }}>
                    {Number.isFinite(loadingProgress.course) ? loadingProgress.course.toFixed(2) : "0.00"}%{" "}
                  </span>{" "}
                  of the { } with average grade{" "}
                  <span style={{ fontWeight: "600" }}>{Number.isFinite(loadingProgress.average) ? loadingProgress.average.toFixed(2) : "0.00"}%</span>
                </h6>
                <button
                  className="primary-button px-3 py-2"
                  onClick={() => {
                    const email = sessionStorage.getItem("student-email") || "";
                    const name = sessionStorage.getItem("student-name") || studentName || "User";
                    navigate("/classes/chat", { state: { email, name } });
                  }}
                >
                  Send Message
                </button>
              </Col>
              <Col md={5}>
                <div className="d-flex justify-content-center align-items-center py-2">
                  <AnimatedCircularProgress
                    percentage={loadingProgress.course}
                    color={getProgressColor(loadingProgress.course)}
                    label="Course Progress"
                    size={110}
                    strokeWidth={10}
                  />
                  <AnimatedCircularProgress
                    percentage={loadingProgress.average}
                    color={getProgressColor(loadingProgress.average)}
                    label="Average Grade"
                    size={110}
                    strokeWidth={10}
                  />
                </div>
              </Col>
            </Row>

            <div style={{ border: "1px solid #dee2e6", borderRadius: "6px", overflow: "hidden" }}>
              <Table className="mb-0" style={{ fontSize: "13px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    <th>Chapter Name</th>
                    <th className="text-center">Progress</th>
                    <th className="text-center">Last Attempt</th>
                    <th className="text-center">Grade(%)</th>
                    <th className="text-center">Due Date</th>
                    <th className="text-center">Letter Grade</th>
                    <th className="text-center">Report</th>
                  </tr>
                </thead>
                <tbody>
                  {lessonsFromAPI.length > 0 && lessonsFromAPI?.map((section, sectionIndex) => (
                    <React.Fragment key={sectionIndex}>
                      <tr>
                        <td onClick={() => toggleSection(section.sectionKey)} style={{ cursor: "pointer", fontWeight: "600", textDecoration: "underline" }}>
                          {expandedSections[section.sectionKey] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}{" "}
                          {section.section}
                        </td>
                        <td className="text-center">
                          <AnimatedCircularProgress
                            color={getProgressColor(section.targetProgress)}
                            percentage={section.targetProgress}
                            size={72}
                          />
                        </td>
                        <td className="text-center">--</td>
                        <td className="text-center">{Number.isFinite(section.targetProgress) ? section.targetProgress.toFixed(2) : "0.00"}%</td>
                        <td className="text-center">--</td>
                        <td className="text-center" style={{ color: section.targetProgress >= 50 ? "#28a745" : "#6c757d" }}>{section.targetProgress >= 50 ? "Pass" : "--"}</td>
                        <td className="text-center">--</td> 
                      </tr>

                      {expandedSections[section.sectionKey] &&
                        section.items.map((item, itemIndex) => (
                          <React.Fragment key={itemIndex}>
                            <tr>
                              <td style={{ paddingLeft: "30px", textDecoration: "underline" }}>{item.name}</td>
                              <td className="text-center">
                                <AnimatedCircularProgress
                                  color={getProgressColor(item.targetProgress)}
                                  percentage={item.targetProgress}
                                  size={72}
                                />
                              </td>
                              <td className="text-center">{item.lastAttempt}</td>
                              <td className="text-center">{Number.isFinite(item.targetProgress) ? item.targetProgress.toFixed(2) : "0.00"}%</td>
                              <td className="text-center" style={{ color: item.dueDate.includes("Oct 30") ? "#dc3545" : "#6c757d" }}>
                                {item.dueDate || "--"}
                              </td>
                              <td className="text-center" style={{ color: item.letterGrade === "Pass" ? "#28a745" : "#6c757d" }}>
                                {item.letterGrade}
                              </td>
                              <td className="text-center">--</td>
                            </tr>
                            {/* Subrubrics */}
                            {item.subrubrics && item.subrubrics.map((subrubric, subIndex) => (
                              <tr key={`${itemIndex}-${subIndex}`}>
                                <td style={{ paddingLeft: "40px", textDecoration: "underline" }}>
                                  {subrubric.subrubric_title}
                                </td>
                                <td className="text-center">--</td>

                                {/* <td className="text-center">
                                  <AnimatedCircularProgress
                                    color={getProgressColor(subrubric.item_score)}
                                    percentage={subrubric.item_score}
                                    size={64}
                                  />
                                </td> */}
                                <td className="text-center">--</td>
                                <td className="text-center">{Number.isFinite(subrubric.subrubric_score) ? subrubric.subrubric_score.toFixed(2) : "0.00"}%</td>
                                <td className="text-center">--</td>
                                <td className="text-center" style={{ color: subrubric.subrubric_score >= 50 ? "#28a745" : "#6c757d" }}>
                                  {subrubric.subrubric_score >= 50 ? "Pass" : "--"}
                                </td>
                                <td className="text-center">
                                  {subrubric.images && Object.keys(subrubric.images).length > 0 ? (
                                    <button 
                                      className="primary-button px-3 py-2"
                                      onClick={() => openReportPopup(subrubric.images.teacher_image_url, subrubric.subrubric_title)}
                                    >
                                      View Report
                                    </button>
                                  ) : (
                                    "--"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  ))}

                  <tr style={{ backgroundColor: "#255A71", color: "white" }}>
                    <td style={{ fontWeight: "600" }}>Course Average</td>
                    <td className="text-center">
                      <AnimatedCircularProgress
                        color={getProgressColor(loadingProgress.average)}
                        percentage={loadingProgress.average}
                        size={72}
                        labelColor="#fff"
                      />
                    </td>
                    <td></td>
                    <td className="text-center" style={{ fontWeight: "600" }}>
                      {Number.isFinite(loadingProgress.average) ? loadingProgress.average.toFixed(2) : "0.00"}%
                    </td>
                    <td></td>
                    <td className="text-center" style={{ fontWeight: "600" }}>
                      {loadingProgress.average >= 50 ? "Pass" : "--"}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Container>
      
      {/* Custom Report Popup */}
      {reportPopup.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] w-[90vw] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Student Attempt - {reportPopup.title}</h3>
              <div
                onClick={closeReportPopup}
                className="p-2 hover:bg-gray-100 rounded-full cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </div>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
              <div className="w-full h-[60vh] overflow-auto border border-gray-200 rounded-lg">
                <img
                  src={reportPopup.imageUrl}
                  alt="Student Attempt"
                  className="w-full h-auto min-h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
